import type { DocumentOcrRetakePayload, SetRedirectTokenRequest } from '@repo/common';

import { rivaasHttp } from './http-client';

import { extractBffErrorFromKyHttpError } from '@/utils/bff-error-mapping';

export type BffClientStep = {
  component: string;
  /** Pre-issued payload for this step when returned from BFF (same contract as `SubmitNextStep.data`). */
  data?: Record<string, unknown>;
  requiresFile: boolean;
  skippable?: boolean;
  step: string;
};

export type BffGetSessionResponse = {
  nextStep: BffClientStep;
  sessionToken: string;
  steps: BffClientStep[];
  workflowId: string;
};

export type BffGetSessionByRedirectTokenResponse = {
  sessionToken: string;
};

export type BffInfoResponse = {
  locale: string;
  logoUrl: string | null;
  rejectedUrl: string;
  steps: BffClientStep[];
  unverifiedUrl: string;
  verifiedUrl: string;
};

export type BffSubmitResponse = {
  error?: {
    code: string;
    details?: Record<string, unknown>;
    message: string;
    requestId?: string;
    retryable?: boolean;
  };
  nextStep: {
    /** Step-specific payload pushed by BFF (e.g. multirange `challengeSequence`, NFC `challenge`). Optional, only present for handlers that need to surface live data to the client. Public BFF contract — see `apps/bff/src/types/index.ts` `SubmitNextStep`. */
    data?: Record<string, unknown>;
    step: string;
  };
  /** Document OCR quality gate requested a recapture (SAIDVU-301). Present without `error` when the user should retry document capture. */
  retake?: DocumentOcrRetakePayload;
  verificationPayload?: Record<string, unknown>;
};

export type PostSubmitOptions = {
  cancel?: boolean;
  clearRedirectToken?: boolean;
  redirectToken?: string;
  skipSteps?: number;
};

/**
 * Multipart filename/part MIME must match payload. DOT liveness `content` is octet-stream (DIS record format), not JPEG/MP4.
 */
function submitFileNameForBlob(file: Blob): string {
  const t = file.type;

  if (t === 'application/octet-stream') {
    return 'liveness-record.bin';
  }

  if (t === 'video/mp4') {
    return 'recording.mp4';
  }

  if (t === 'video/webm') {
    return 'recording.webm';
  }

  if (t === 'video/quicktime') {
    return 'recording.mov';
  }

  if (t === 'image/png') {
    return 'image.png';
  }

  if (t === 'image/webp') {
    return 'image.webp';
  }

  return 'image.jpg';
}

export function postSubmit(file?: Blob, options: PostSubmitOptions = {}) {
  const { cancel, clearRedirectToken, redirectToken, skipSteps } = options;
  const formData = new FormData();

  if (file) {
    formData.append('file', file, submitFileNameForBlob(file));
  }

  if (redirectToken) {
    formData.append('redirectToken', redirectToken);
  }

  if (skipSteps) {
    formData.append('skipSteps', String(skipSteps));
  }

  if (clearRedirectToken) {
    formData.append('clearRedirectToken', 'true');
  }

  if (cancel) {
    formData.append('cancel', 'true');
  }

  return rivaasHttp.post('submit', { body: formData }).json<BffSubmitResponse>();
}

export async function postSubmitHandled(file?: Blob, options: PostSubmitOptions = {}) {
  try {
    return await postSubmit(file, options);
  } catch (err: unknown) {
    const extracted = await extractBffErrorFromKyHttpError(err);

    return {
      nextStep: { step: 'fail' },
      error: {
        code: extracted?.code ?? 'INTERNAL_SERVER_ERROR',
        message: extracted?.message ?? 'An unexpected error occurred.',
        retryable: extracted?.retryable,
        details: extracted?.details,
      },
    } as BffSubmitResponse;
  }
}

export function getInfo() {
  return rivaasHttp.get('info').json<BffInfoResponse>();
}

export function getSessionByRedirectToken(redirectToken: string) {
  return rivaasHttp.get(`session/redirect-token/${redirectToken}`).json<BffGetSessionByRedirectTokenResponse>();
}

export async function postRedirectToken({ redirectToken }: SetRedirectTokenRequest['body']) {
  await postSubmit(undefined, { redirectToken });
}
